/**
 * Applies platform light theme on marketing + onboarding pages.
 * Source priority:
 * 1. GET /api/users/me/ui-preferences → theme.config.light (when access/context token present)
 * 2. GET /api/platform/themes/public light config
 * 3. Built-in default light tokens
 */

import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { themeService } from '@/services/api/platform/theme.service'
import { useUIPreferences } from '@/contexts/UIPreferencesContext'
import {
  applyDefaultThemeTokens,
  applyThemeConfig,
  type DatabaseTheme,
} from '@/utils/theme-application.util'
import { usesPublicPlatformTheme } from '@/utils/route.util'

export const PUBLIC_THEME_QUERY_KEY = ['platform-themes', 'public'] as const

/**
 * Fetch + apply light theme tokens on marketing pages and /onboarding.
 */
export function usePublicLandingTheme() {
  const { pathname } = useLocation()
  const usePublicTheme = usesPublicPlatformTheme(pathname)
  const { uiPreferences } = useUIPreferences()

  const uiTheme = (uiPreferences?.theme as DatabaseTheme | null | undefined) ?? null

  const { data: publicTheme } = useQuery({
    queryKey: PUBLIC_THEME_QUERY_KEY,
    queryFn: () => themeService.getPublicTheme(),
    enabled: usePublicTheme,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  })

  useLayoutEffect(() => {
    if (!usePublicTheme) return
    document.documentElement.classList.remove('dark')

    const themeToApply = uiTheme ?? publicTheme ?? null
    if (themeToApply) {
      applyThemeConfig(themeToApply, 'light')
    } else {
      applyDefaultThemeTokens('light')
    }
  }, [usePublicTheme, uiTheme, publicTheme])
}
